import React, { useEffect } from 'react';
import { useOrganizationClientRecords } from '../../../../../../../hooks/api/useOrganizationClientRecords';
import { OrganizationTypes } from '../../../../../../../primary-record-types';
import Stack, { ContentStack } from '../../../../../../v2BaseComponents/Stack';
import { AnimateLoadedContent } from '../../../../../../v2BaseComponents/Loader';
import { clientRecordStatusTypeGuard } from '../../../../../../../utils/clientRecordsUtil';
import { getIntl } from '../../../../../../../utils/intl';
import InfoIcon from '../../../../../../v2BaseComponents/InfoIcon';
import { PromptActions } from '../../../../../../v2BaseComponents/Prompt';
import getName from '../../../../../../../utils/getName';

const intl = getIntl('deactivate-client-prompt-content', {
    deactivateClient: 'Deactivate Client',
    deactivateClientPrompt:
        "Are you sure you want to deactivate {clientName}?  This will end this organization's service with this client and you will no longer have access to this client's data.  This action cannot be undone.",
    thisClient: 'this client',
    deactivate: 'Deactivate',
    cancel: 'Cancel',
});

export type DeactivateClientPromptContentOwnProps = {
    onClose: () => void;
    organizationClientRecordId: string | undefined;
    onDeactivate: (medicalProfileId: string) => void;
};

export type DeactivateClientPromptContentProps = DeactivateClientPromptContentOwnProps;

const Content: React.FC<{
    clientRecord: OrganizationTypes.OrganizationClientRecordForStatus<'active'>;
    onDeactivateClient: () => void;
    onClose: () => void;
}> = ({ clientRecord, onDeactivateClient: onReEngageClientRecord, onClose }) => (
    <ContentStack>
        <InfoIcon
            alertIconProps={{
                name: 'UserX',
                variant: 'error',
            }}
            title={intl('deactivateClient')}
            subText={intl('deactivateClientPrompt', {
                clientName: getName(clientRecord.clientDetails) || intl('thisClient'),
            })}
        />
        <PromptActions
            actions={[
                {
                    title: intl('deactivate'),
                    onPress: onReEngageClientRecord,
                    icon: 'UserX',
                    variant: 'alert',
                    testID: 'confirm-deactivate-client',
                },
                {
                    title: intl('cancel'),
                    onPress: onClose,
                    variant: 'secondary',
                    icon: 'XCircle',
                    testID: 'cancel-deactivate-client',
                },
            ]}
        />
    </ContentStack>
);

const DeactivateClientPromptContent: React.FC<DeactivateClientPromptContentProps> = ({
    onClose,
    organizationClientRecordId,
    onDeactivate,
}) => {
    const { isLoadingClientRecord, clientRecord, isArchivingClientRecord } = useOrganizationClientRecords({
        organizationClientRecordId,
    });

    useEffect(() => {
        if (clientRecord && !clientRecordStatusTypeGuard.active(clientRecord)) {
            onClose();
        }
    }, [clientRecord, onClose]);

    const isLoading =
        isLoadingClientRecord ||
        !clientRecord ||
        isArchivingClientRecord ||
        !clientRecordStatusTypeGuard.active(clientRecord);

    return (
        <Stack vertical fullWidth style={{ minHeight: 200 }}>
            <AnimateLoadedContent isLoading={isLoading}>
                {() =>
                    clientRecord && clientRecordStatusTypeGuard.active(clientRecord) ? (
                        <Content
                            clientRecord={clientRecord}
                            onDeactivateClient={() => {
                                if (clientRecord && clientRecordStatusTypeGuard.active(clientRecord)) {
                                    onDeactivate(clientRecord.clientMedicalProfileId);
                                }
                            }}
                            onClose={onClose}
                        />
                    ) : null
                }
            </AnimateLoadedContent>
        </Stack>
    );
};

export default React.memo(DeactivateClientPromptContent);
